<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type { CreateDeptRequest, Dept, UpdateDeptRequest } from '../../types'

interface DeptRow extends Dept {
  level: number
  childCount: number
  parentName: string
}

const props = defineProps<{
  depts: Dept[]
  canCreate: boolean
  canUpdate: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateDeptRequest]
  update: [deptId: number, payload: UpdateDeptRequest]
}>()

const showCreateForm = ref(false)
const editingDeptId = ref<number | null>(null)
const filters = ref({
  keyword: '',
  status: 'ALL' as 'ALL' | 'ENABLED' | 'DISABLED'
})
const form = ref({
  parentId: 0,
  code: 'support',
  name: '客户成功部',
  sortOrder: 30
})
const editForm = ref({
  parentId: 0,
  name: '',
  sortOrder: 100,
  enabled: true
})

const deptRows = computed<DeptRow[]>(() =>
  buildDeptTree(props.depts).map((node) => ({
    ...node.dept,
    level: node.level,
    childCount: node.childCount,
    parentName: node.dept.parentId
      ? props.depts.find((item) => item.id === node.dept.parentId)?.name ?? `部门 ${node.dept.parentId}`
      : '顶级部门'
  }))
)

const filteredDepts = computed(() => {
  const keyword = filters.value.keyword.trim().toLowerCase()
  return deptRows.value.filter((dept) => {
    const matchesKeyword =
      keyword.length === 0 ||
      dept.name.toLowerCase().includes(keyword) ||
      dept.code.toLowerCase().includes(keyword) ||
      dept.parentName.toLowerCase().includes(keyword)
    const matchesStatus =
      filters.value.status === 'ALL' ||
      (filters.value.status === 'ENABLED' && dept.enabled) ||
      (filters.value.status === 'DISABLED' && !dept.enabled)
    return matchesKeyword && matchesStatus
  })
})

const parentDeptOptions = computed(() => [
  { label: '顶级部门', value: 0 },
  ...props.depts.map((d) => ({ label: d.name, value: d.id }))
])

const statusOptions = [
  { label: '全部状态', value: 'ALL' },
  { label: '启用', value: 'ENABLED' },
  { label: '停用', value: 'DISABLED' }
]

const columns: TableColumnType<DeptRow>[] = [
  { key: 'name', title: '部门', minWidth: 200 },
  { key: 'parent', title: '上级部门', minWidth: 160 },
  { key: 'sortOrder', title: '排序', width: 90 },
  { key: 'status', title: '状态', width: 90 },
  { key: 'actions', title: '操作', width: 140, align: 'right' }
]

function resetFilters() {
  filters.value = {
    keyword: '',
    status: 'ALL'
  }
}

function createDept() {
  if (!props.canCreate || !form.value.code || !form.value.name) return
  emit('create', {
    parentId: form.value.parentId === 0 ? null : form.value.parentId,
    code: form.value.code,
    name: form.value.name,
    sortOrder: Number(form.value.sortOrder)
  })
  showCreateForm.value = false
}

function startEdit(dept: Dept) {
  editingDeptId.value = dept.id
  editForm.value = {
    parentId: dept.parentId ?? 0,
    name: dept.name,
    sortOrder: dept.sortOrder,
    enabled: dept.enabled
  }
}

function cancelEdit() {
  editingDeptId.value = null
}

function updateDept(deptId: number) {
  if (!props.canUpdate || !editForm.value.name) return
  emit('update', deptId, {
    parentId: editForm.value.parentId === 0 ? null : editForm.value.parentId,
    name: editForm.value.name,
    sortOrder: Number(editForm.value.sortOrder),
    enabled: editForm.value.enabled
  })
  editingDeptId.value = null
}

function editParentOptions(currentDeptId: number) {
  const available = availableParentDepts(currentDeptId)
  return [
    { label: '顶级部门', value: 0 },
    ...available.map((d) => ({ label: d.name, value: d.id }))
  ]
}

function availableParentDepts(currentDeptId: number | string) {
  const currentKey = String(currentDeptId)
  const blockedIds = new Set([currentKey, ...descendantDeptIds(currentDeptId).map(String)])
  return props.depts.filter((dept) => !blockedIds.has(String(dept.id)))
}

function descendantDeptIds(parentId: number | string) {
  const childrenByParent = new Map<string, Dept[]>()
  for (const dept of props.depts) {
    if (dept.parentId == null) continue
    const key = String(dept.parentId)
    childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), dept])
  }
  const ids: Array<number | string> = []
  const visit = (deptId: number | string) => {
    for (const child of childrenByParent.get(String(deptId)) ?? []) {
      ids.push(child.id)
      visit(child.id)
    }
  }
  visit(parentId)
  return ids
}

function buildDeptTree(depts: Dept[]) {
  const childrenByParent = new Map<string | null, Dept[]>()
  for (const dept of depts) {
    const key = dept.parentId != null && String(dept.parentId).trim() !== ''
      ? String(dept.parentId)
      : null
    childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), dept])
  }
  for (const children of childrenByParent.values()) {
    children.sort((left, right) => (Number(left.sortOrder) || 0) - (Number(right.sortOrder) || 0) || String(left.id).localeCompare(String(right.id)))
  }
  const rows: Array<{ dept: Dept; level: number; childCount: number }> = []
  const visited = new Set<string>()
  const visit = (parentId: string | null, level: number) => {
    for (const dept of childrenByParent.get(parentId) ?? []) {
      const selfKey = String(dept.id)
      if (visited.has(selfKey)) continue
      visited.add(selfKey)
      rows.push({
        dept,
        level,
        childCount: childrenByParent.get(selfKey)?.length ?? 0
      })
      visit(selfKey, level + 1)
    }
  }
  visit(null, 0)
  for (const dept of depts) {
    const selfKey = String(dept.id)
    if (!visited.has(selfKey)) {
      rows.push({
        dept,
        level: 0,
        childCount: childrenByParent.get(selfKey)?.length ?? 0
      })
    }
  }
  return rows
}
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Department Management</p>
        <h2>部门列表</h2>
      </div>
      <span>{{ filteredDepts.length }} / {{ depts.length }} 个部门</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="resetFilters">
      <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="部门名称、编码、上级部门" />
      <RrSelect v-model:value="filters.status" :options="statusOptions" />
      <template #actions>
        <RrButton type="default" :disabled="loading" @click="resetFilters">重置</RrButton>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="showCreateForm = !showCreateForm">
          {{ showCreateForm ? '收起新增' : '新增部门' }}
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showCreateForm"
      title="新增部门"
      :description="canCreate ? '可写' : '只读'"
      @close="showCreateForm = false"
    >
      <RrForm :disabled="!canCreate || loading" @submit.prevent="createDept">
        <RrFormItem label="上级部门">
          <RrSelect v-model:value.number="form.parentId" :options="parentDeptOptions" />
        </RrFormItem>
        <RrFormItem label="部门编码">
          <RrInput v-model:value.trim="form.code" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="部门名称">
          <RrInput v-model:value.trim="form.name" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="排序">
          <RrInput v-model:value.number="form.sortOrder" type="number" min="0" max="9999" />
        </RrFormItem>
      </RrForm>
      <!-- 按钮放在抽屉 footer，确保公共表单不吞掉 footer 插槽。 -->
      <template #footer>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="createDept">创建部门</RrButton>
      </template>
    </AdminDrawer>

    <AdminDataTable
      :columns="columns"
      :data-source="filteredDepts"
      :pagination="false"
      :loading="loading"
      row-key="id"
      :count="filteredDepts.length"
      :empty="filteredDepts.length === 0"
      empty-title="暂无匹配部门。"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'name'">
          <template v-if="editingDeptId === record.id">
            <RrInput v-model:value.trim="editForm.name" autocomplete="off" />
            <small style="display: block; color: var(--rr-color-text-muted);">{{ record.code }} · ID {{ record.id }}</small>
          </template>
          <template v-else>
            <strong :style="{ paddingLeft: `${record.level * 18}px` }">
              <span style="display: inline-block; width: 14px;">{{ record.childCount ? '▾' : '·' }}</span>
              {{ record.name }}
            </strong>
            <small style="display: block; color: var(--rr-color-text-muted); padding-left: `${record.level * 18 + 14}px`">{{ record.code }} · ID {{ record.id }}</small>
          </template>
        </template>
        <template v-else-if="column.key === 'parent'">
          <template v-if="editingDeptId === record.id">
            <RrSelect v-model:value.number="editForm.parentId" :options="editParentOptions(record.id)" />
          </template>
          <template v-else>
            <strong>{{ record.parentName }}</strong>
            <small style="display: block; color: var(--rr-color-text-muted);">{{ record.parentId ? `上级 ID ${record.parentId}` : '组织根节点' }}</small>
          </template>
        </template>
        <template v-else-if="column.key === 'sortOrder'">
          <RrInput
            v-if="editingDeptId === record.id"
            v-model:value.number="editForm.sortOrder"
            type="number"
            min="0"
            max="9999"
          />
          <template v-else>{{ record.sortOrder }}</template>
        </template>
        <template v-else-if="column.key === 'status'">
          <RrCheckbox v-if="editingDeptId === record.id" v-model:checked="editForm.enabled">
            {{ editForm.enabled ? '启用' : '停用' }}
          </RrCheckbox>
          <AdminStatusTag v-else :tone="record.enabled ? 'success' : 'neutral'">
            {{ record.enabled ? '启用' : '停用' }}
          </AdminStatusTag>
        </template>
        <template v-else-if="column.key === 'actions'">
          <span class="row-actions compact-actions">
            <template v-if="editingDeptId === record.id">
              <RrButton type="primary" :disabled="!canUpdate || loading" @click="updateDept(record.id)">保存</RrButton>
              <RrButton type="default" :disabled="loading" @click="cancelEdit">取消</RrButton>
            </template>
            <template v-else>
              <RrButton type="link" :disabled="!canUpdate || loading" @click="startEdit(record)">编辑</RrButton>
            </template>
          </span>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
